import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { JwtPayload } from "jwt-decode";
import ReactGA from "react-ga4";
import Clarity from "@microsoft/clarity";

import Profile from "./components/UserProfile/Profile";
import Register from "./components/Account/Register";
import SidebarLayout from "./components/SideBar/Sidebar";
import Home from "./components/SideBar/Home";
import UpdateUserProfile from "./components/UserProfile/UpdateUserProfile";
import AddNewAddress from "./components/Address/AddNewAddress";
import AddressDetails from "./components/Address/AddressDetails";
import EditAddress from "./components/Address/EditAddress";
import About from "./components/More/About";
import Signin from "./components/Account/Signin";
import Favourites from "./components/SideBar/Favourite";
import FeedBack from "./components/More/FeedBack";
import ReportIssue from "./components/More/ReportIssue";
import Dashboard from "./components/AdminPages/DashBoard";
import ManageUser from "./components/AdminPages/ManageUser";
import ViewFeedBack from "./components/AdminPages/ViewFeedBack";
import ViewReportIssue from "./components/AdminPages/ViewReportIssue";
import UserDetails from "./components/AdminPages/UserDetails";
import FeedbackDetails from "./components/AdminPages/FeedBackDetails";
import ReportIssueDetails from "./components/AdminPages/ReportIssueDetails";
import ViewDeleteRequest from "./components/AdminPages/ViewDeleteRequest";
import DeleteRequestDetails from "./components/AdminPages/DeleteRequestDetails";
import SearchSummary from "./components/AdminPages/SearchSummary";
import ReferralDetails from "./components/AdminPages/ReferralDetails";
import { MapSettingsProvider } from "./components/Context/MapSettingsContext";
import SearchAddressDetails from "./components/Address/SearchAddressDetails";
import FavouriteAddressDetails from "./components/Address/FavouriteAddressDetails";
import AddressList from "./components/SideBar/AddressList";
import More from "./components/SideBar/More";
import { LocalizationProvider } from "./components/Context/MultiLanguageContext";
import PrivateRoute from "./components/PrivateRoute/PrivateRoute";
import AddressView from "./components/Address/AddressView";
import DynamicAddressRoute from "./components/Routes/DynamicAddressRoute";
import ReferralEachUser from "./components/AdminPages/ReferralEachUser";
import ManageAddress from "./components/AdminPages/ManageAddress";
import ManageApiUser from "./components/AdminPages/ManageApiUser";
import PamDetails from "./components/AdminPages/PamcodeDetails";
import EditAddressAdmin from "./components/AdminPages/EditAddressAdmin";

const TRACKING_ID = "G-6QWBHH4X4Q";
ReactGA.initialize(TRACKING_ID);

const CLARITY_PROJECT_ID = "r7gioagzkz";

const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    if (decoded.exp) {
      return decoded.exp * 1000 < Date.now();
    }
    return true;
  } catch (error) {
    console.error('Error decoding token:', error);
    return true;
  }
};

const App: React.FC = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState(localStorage.getItem('userName') || '');

  const updateUserName = (newUserName: string) => {
    setUserName(newUserName);
    if (newUserName) {
      localStorage.setItem('userName', newUserName);
    }
  };

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      Clarity.init(CLARITY_PROJECT_ID);
    }
    const token = localStorage.getItem("authToken");
    if (token && isTokenExpired(token)) {
      localStorage.removeItem("authToken");
      alert("Session expired. Please log in again.");
      navigate("/", { replace: true });
    }

    const handleEnterKey = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        const activeElement = document.activeElement;
        if (activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement) {
          const submitButton = document.querySelector('button[type="submit"]') as HTMLButtonElement;
          submitButton?.click();
        }
      }
    };

    document.addEventListener("keydown", handleEnterKey);

    return () => {
      document.removeEventListener("keydown", handleEnterKey);
    };
  }, [navigate]);

  return (
    <LocalizationProvider>
      <Routes>
        <Route path="/" element={<Signin />} />
        <Route path="/register" element={<Register updateUserName={updateUserName} />} />

        {/* Protected routes with sidebar */}
        <Route
          element={
            <PrivateRoute>
              <SidebarLayout />
            </PrivateRoute>
          }
        >
          <Route path="/home" element={<Home updateUserName={updateUserName} />} />
          <Route path="/profile" element={<Profile updateUserName={updateUserName} />} />
          <Route path="/addresslist" element={<AddressList updateUserName={updateUserName} />} />
          <Route path="/favourites" element={<Favourites updateUserName={updateUserName} />} />
          <Route path="/about" element={<About updateUserName={updateUserName} />} />
          <Route path="/more" element={<More updateUserName={updateUserName} />} />
          <Route path="/add-new-address" element={<AddNewAddress updateUserName={updateUserName} />} />
          <Route path="/referrals" element={<ReferralDetails />} />
          <Route path="/address-details/:id" element={<AddressDetails updateUserName={updateUserName} />} />
          <Route path="/search-address-details/:id" element={<SearchAddressDetails updateUserName={updateUserName} />} />
          <Route path="/search-address-details/:addressId" element={<AddressDetails updateUserName={updateUserName} />} />
          <Route path="/favourite-address-details/:id" element={<FavouriteAddressDetails updateUserName={updateUserName} />} />
          <Route path="/edit-address/:id" element={<EditAddress updateUserName={updateUserName} />} />
          <Route path="/update-user-profile" element={<UpdateUserProfile updateUserName={updateUserName} />} />
          <Route path="/feedback" element={<FeedBack updateUserName={updateUserName} />} />
          <Route path="/report-issue" element={<ReportIssue updateUserName={updateUserName} />} />
          <Route path="/referral-user/:userId" element={<ReferralEachUser />} />
          
          {/* Protected AddressView route - with sidebar for authenticated users */}
          <Route
            path="/address/:pamCode"
            element={
              <DynamicAddressRoute
                updateUserName={updateUserName}
                showSidebar={true}
                showHeader={true}
              />
            }
          />

          {/* Admin routes */}
          <Route path="/DashBoard" element={<Dashboard />} />
          <Route path="/ManageUser" element={<ManageUser />} />
          <Route path="/manageaddress" element={<ManageAddress />} />
          <Route path="/manage-api-user" element={<ManageApiUser />} />
          <Route path="/ViewFeedback" element={<ViewFeedBack />} />
          <Route path="/ViewReportIssue" element={<ViewReportIssue />} />
          <Route path="/search-summary" element={<SearchSummary />} />
          <Route path="/user-details/:userId" element={<UserDetails />} />
          <Route path="/feedback-details/:index" element={<FeedbackDetails />} />
          <Route path="/report-issue-details/:index" element={<ReportIssueDetails />} />
          <Route path="/view-delete-request/" element={<ViewDeleteRequest />} />
          <Route path="/delete-request-details/:index" element={<DeleteRequestDetails />} />
          <Route path="/referral-details" element={<ReferralDetails />} />
          <Route path="/pamcode-details/:pamCode" element={<PamDetails />} />
          <Route path="/edit-address-admin/:id"element={<EditAddressAdmin updateUserName={updateUserName}/>}
/>
        </Route>

        {/* Public AddressView route - for non-authenticated users */}
        <Route
          path="/:pamCode"
          element={
            <DynamicAddressRoute
              updateUserName={updateUserName}
              showSidebar={false}
              showHeader={false}
            />
          }
        />
      </Routes>
    </LocalizationProvider>
  );
};

const Root: React.FC = () => {
  return (
    <Router>
      <MapSettingsProvider>
        <App />
      </MapSettingsProvider>
    </Router>
  );
};

export default Root;
